import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { ClientCardScreen } from '@/features/clients/client-card-screen'
import { PassActionsDialog } from '@/features/passes/pass-actions-dialog'
import type { PassActionsMode } from '@/features/passes/use-pass-actions'

export const Route = createFileRoute('/_app/clients/$clientId')({ component: ClientPage })

/// Картка клієнта і діалог дій з абонементом.
///
/// Діалог живе тут, а не всередині картки: заморозка й продовження — дії над
/// абонементом, а не частина подання людини, і той самий діалог відкривається з
/// інших місць. `refund` сюди НЕ веде — повернення це чек і гроші, окремий
/// екран (refund.md).
///
/// Доти з цього робився висновок «кнопка нічого не робить, і це краще за
/// діалог, який виглядає як повернення і ним не є». Половина висновку була
/// правильна, друга протухла: екран повернення вже існує. Виходило гірше за
/// обидва варіанти — рецепції казали «кличте менеджера», менеджер підходив,
/// тиснув єдину дозволену йому дію, і не ставалось нічого (D71).
///
/// Тепер кнопка веде туди, де повернення справді оформлюють, — до чеків зміни
/// на касі. По абонементу чек не знайти (у моці звʼязку «абонемент → чек»
/// немає, та й у житті його дає сервер), тож людину приводять до списку, а не
/// до готового чека: обрати потрібний — робота рецепції, і вона це вміє.
function ClientPage() {
  const { clientId } = Route.useParams()
  const navigate = useNavigate()
  const [action, setAction] = useState<{ passId: string; mode: PassActionsMode } | null>(null)

  const handlePassAction = (intent: 'freeze' | 'extend' | 'refund', passId: string) => {
    if (intent === 'refund') {
      void navigate({ to: '/pos', search: { client: clientId, refund: '1' } })
      return
    }
    setAction({ passId, mode: intent })
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) setAction(null)
  }

  /// Продовження грошей не бере: воно віддає касі передзаповнений кошик.
  ///
  /// В адресу їде ідентифікатор варіанта, а не сума: «На касу · 4 900,00 ₴» —
  /// це гроші, і брати їх із URL каса не буде. Доти сюди не їхало нічого, і
  /// рецепціоніст при клієнті набирав тариф наново, з памʼяті.
  /// «Продати · F2» — головна дія картки, і доти вона не мала адресата: проп у
  /// роут не передали, `onSell?.()` мовчки нічого не робив. Рецепція тисла
  /// єдину амберну кнопку екрана, нічого не ставалось, і вона йшла на касу
  /// шукати ту саму людину вдруге — при ній же. Рівно той сценарій, який
  /// закривав D30.
  ///
  /// Механізм був написаний десятьма рядками нижче: каса приймає `?client=` в
  /// адресі. Позиція НЕ передається — дія називає лише людину (D30: «позиція
  /// лише коли дія її називає»), а що саме продавати, рецепція обере на касі.
  const handleSell = () => {
    void navigate({ to: '/pos', search: { client: clientId } })
  }

  const handleSendToCheckout = ({ offerId }: { passId: string; offerId: string }) => {
    setAction(null)
    void navigate({ to: '/pos', search: { offer: offerId, client: clientId } })
  }

  return (
    <>
      <ClientCardScreen
        clientId={clientId}
        onPassAction={handlePassAction}
        onSell={handleSell}
      />
      {action ? (
        <PassActionsDialog
          passId={action.passId}
          mode={action.mode}
          open
          onOpenChange={handleOpenChange}
          onSendToCheckout={handleSendToCheckout}
        />
      ) : null}
    </>
  )
}
